import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  addDays,
  BAR_COLOR,
  DAY_WIDTH,
  diffDays,
  isoDay,
  LABEL_WIDTH,
  ROW_HEIGHT,
  startOfDay,
  type TimelineDependencyEdge,
  type TimelineRow,
  type TimelineSection,
  type ZoomLevel,
} from "../../utils/timeline-geometry";
import { cn, cardClassName } from '@jira-clone/ui';

const ISSUE_TYPE_META: Record<string, { label: string; color: string; icon: string }> = {
  epic: { label: "Epic", color: "bg-purple-100 text-purple-700", icon: "⚡" },
  story: { label: "Story", color: "bg-green-100 text-green-700", icon: "◆" },
  task: { label: "Task", color: "bg-blue-100 text-blue-700", icon: "✓" },
  bug: { label: "Bug", color: "bg-red-100 text-red-700", icon: "●" },
  subtask: { label: "Subtask", color: "bg-muted text-muted-foreground", icon: "↳" },
};

interface GanttChartProps {
  sections: TimelineSection[];
  dependencies: TimelineDependencyEdge[];
  zoom: ZoomLevel;
  /** Persists a drag-to-reschedule/resize, keyed by row id (an issue/task id either way). */
  onReschedule: (rowId: string, dates: { startDate: string; dueDate: string }) => Promise<void>;
  emptyMessage?: string;
}

/**
 * Direct port of JIRA-ZYNX GanttChart component (Phase 12).
 * Preserves the exact SVG dependencies, zoom grid calculation, drag-to-reschedule/resize,
 * today indicator, and collapsible epic hierarchy.
 */
export function GanttChart({ sections, dependencies, zoom, onReschedule, emptyMessage }: GanttChartProps) {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [drag, setDrag] = useState<{ rowId: string; mode: "move" | "resize-start" | "resize-end"; startX: number; origStart: Date; origDue: Date } | null>(null);
  const [dragPreview, setDragPreview] = useState<{ rowId: string; startDate: string; dueDate: string } | null>(null);

  const allRows = useMemo(() => sections.flatMap((s) => s.rows), [sections]);
  const rowById = useMemo(() => new Map(allRows.map((r) => [r.id, r])), [allRows]);

  const { rangeStart, totalDays } = useMemo(() => {
    const dated = allRows.filter((r) => r.startDate && r.dueDate);
    const today = startOfDay(new Date());
    let min = addDays(today, -14);
    let max = addDays(today, 45);
    for (const r of dated) {
      const s = startOfDay(new Date(r.startDate!));
      const d = startOfDay(new Date(r.dueDate!));
      if (s < min) min = s;
      if (d > max) max = d;
    }
    return { rangeStart: min, totalDays: diffDays(max, min) + 1 };
  }, [allRows]);

  const dayWidth = DAY_WIDTH[zoom];
  const chartWidth = totalDays * dayWidth;

  function dateToX(d: Date): number {
    return diffDays(d, rangeStart) * dayWidth;
  }

  function toggleCollapsed(sectionId: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  }

  function startDrag(e: React.MouseEvent, row: TimelineRow, mode: "move" | "resize-start" | "resize-end") {
    if (!row.draggable || !row.startDate || !row.dueDate) return;
    e.preventDefault();
    setDrag({ rowId: row.id, mode, startX: e.clientX, origStart: new Date(row.startDate), origDue: new Date(row.dueDate) });
    setDragPreview({ rowId: row.id, startDate: row.startDate, dueDate: row.dueDate });
  }

  useEffect(() => {
    if (!drag) return;

    function onMove(e: MouseEvent) {
      if (!drag) return;
      const deltaDays = Math.round((e.clientX - drag.startX) / dayWidth);
      let newStart = drag.origStart;
      let newDue = drag.origDue;
      if (drag.mode === "move") {
        newStart = addDays(drag.origStart, deltaDays);
        newDue = addDays(drag.origDue, deltaDays);
      } else if (drag.mode === "resize-start") {
        newStart = addDays(drag.origStart, deltaDays);
        if (newStart > drag.origDue) newStart = drag.origDue;
      } else if (drag.mode === "resize-end") {
        newDue = addDays(drag.origDue, deltaDays);
        if (newDue < drag.origStart) newDue = drag.origStart;
      }
      setDragPreview({ rowId: drag.rowId, startDate: isoDay(newStart), dueDate: isoDay(newDue) });
    }

    async function onUp() {
      if (drag && dragPreview) {
        await onReschedule(drag.rowId, { startDate: dragPreview.startDate, dueDate: dragPreview.dueDate }).catch(() => {});
      }
      setDrag(null);
      setDragPreview(null);
    }

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [drag, dragPreview, dayWidth, onReschedule]);

  const visibleRows: { row: TimelineRow; y: number }[] = [];
  {
    let y = 0;
    for (const section of sections) {
      y += ROW_HEIGHT;
      if (collapsed.has(section.id)) continue;
      for (const row of section.rows) {
        visibleRows.push({ row, y });
        y += ROW_HEIGHT;
      }
    }
  }
  const chartHeight = (() => {
    let y = 0;
    for (const section of sections) {
      y += ROW_HEIGHT;
      if (!collapsed.has(section.id)) y += section.rows.length * ROW_HEIGHT;
    }
    return y;
  })();

  const yByRowId = new Map(visibleRows.map((v) => [v.row.id, v.y]));

  const visibleDependencies = useMemo(
    () => dependencies.filter((d) => yByRowId.has(d.blockerId) && yByRowId.has(d.blockedId)),
    [dependencies, yByRowId]
  );

  function barGeometry(row: TimelineRow): { x: number; width: number } | null {
    const preview = dragPreview?.rowId === row.id ? dragPreview : null;
    const start = preview?.startDate ?? row.startDate;
    const due = preview?.dueDate ?? row.dueDate;
    if (!start || !due) return null;
    const x = dateToX(new Date(start));
    const width = Math.max(dayWidth, dateToX(new Date(due)) - x + dayWidth);
    return { x, width };
  }

  const todayX = dateToX(startOfDay(new Date()));

  if (allRows.length === 0) {
    return <p className="text-sm text-muted-foreground p-4">{emptyMessage ?? "No epics or tasks yet — create some to see them on the timeline."}</p>;
  }

  return (
    <div className={cn(cardClassName(), 'overflow-auto')}>
      <div className="flex" style={{ minWidth: LABEL_WIDTH + chartWidth }}>
        {/* Label column */}
        <div className="sticky left-0 z-20 shrink-0 border-r border-border bg-card shadow-sm" style={{ width: LABEL_WIDTH }}>
          <div className="flex items-center border-b border-border px-3 text-[10px] font-bold uppercase tracking-wider text-muted-foreground" style={{ height: ROW_HEIGHT }}>
            Task / Epic
          </div>
          {sections.map((section) => (
            <div key={section.id}>
              <button
                onClick={() => toggleCollapsed(section.id)}
                className="flex w-full items-center gap-1.5 border-b border-border/50 px-3 text-left text-xs font-semibold text-foreground hover:bg-muted/50 transition-colors"
                style={{ height: ROW_HEIGHT }}
              >
                <span className="text-muted-foreground font-mono">{collapsed.has(section.id) ? "▸" : "▾"}</span>
                <span className="truncate">{section.label}</span>
              </button>
              {!collapsed.has(section.id) &&
                section.rows.map((row) => (
                  <div
                    key={row.id}
                    className="flex items-center gap-1.5 border-b border-border/40 px-3 text-xs text-foreground/80 hover:bg-muted/30"
                    style={{ height: ROW_HEIGHT, paddingLeft: row.indent ? 28 : 12 }}
                  >
                    <span title={ISSUE_TYPE_META[row.type]?.label ?? row.type}>
                      {ISSUE_TYPE_META[row.type]?.icon ?? "•"}
                    </span>
                    {row.badge && (
                      <span
                        className="shrink-0 rounded px-1.5 py-0.5 text-[9px] font-bold uppercase leading-none text-white shadow-xs"
                        style={{ backgroundColor: row.badge.color }}
                        title={row.badge.label}
                      >
                        {row.badge.label}
                      </span>
                    )}
                    <Link to={row.linkTo} className="truncate hover:text-primary hover:underline transition-colors">
                      <span className="mr-1 text-muted-foreground font-mono text-[11px]">{row.key}</span>
                      {row.title}
                    </Link>
                  </div>
                ))}
            </div>
          ))}
        </div>

        {/* Chart area */}
        <div className="relative" style={{ width: chartWidth }}>
          {/* Header gridlines */}
          <div className="sticky top-0 z-10 flex border-b border-border bg-card" style={{ height: ROW_HEIGHT }}>
            {Array.from({ length: totalDays }).map((_, i) => {
              const d = addDays(rangeStart, i);
              const showLabel = zoom === "week" ? true : zoom === "month" ? d.getDate() === 1 || i === 0 : d.getDate() <= 7 && d.getDay() === 1;
              return (
                <div
                  key={i}
                  className="shrink-0 border-r border-border/30 text-center text-[10px] font-medium text-muted-foreground flex items-center justify-center"
                  style={{ width: dayWidth }}
                >
                  {showLabel ? (zoom === "week" ? d.getDate() : d.toLocaleDateString(undefined, { month: "short", day: "numeric" })) : ""}
                </div>
              );
            })}
          </div>

          <div className="relative" style={{ height: chartHeight }}>
            {/* Vertical gridlines */}
            <div className="absolute inset-0 flex pointer-events-none">
              {Array.from({ length: totalDays }).map((_, i) => (
                <div key={i} className="shrink-0 border-r border-border/20" style={{ width: dayWidth }} />
              ))}
            </div>

            {/* Today marker */}
            <div className="absolute top-0 bottom-0 w-[2px] bg-rose-500 z-10 shadow-xs" style={{ left: todayX + dayWidth / 2 }} title="Today" />

            {/* Section rows + bars */}
            {(() => {
              let y = 0;
              const elements: React.ReactNode[] = [];
              for (const section of sections) {
                elements.push(<div key={`${section.id}-hdr`} className="absolute border-b border-border/50 bg-muted/20" style={{ top: y, left: 0, right: 0, height: ROW_HEIGHT }} />);
                y += ROW_HEIGHT;
                if (collapsed.has(section.id)) continue;
                for (const row of section.rows) {
                  const geom = barGeometry(row);
                  const color = BAR_COLOR[row.type] ?? "#6366f1";

                  elements.push(
                    <div key={row.id} className="absolute border-b border-border/30" style={{ top: y, left: 0, right: 0, height: ROW_HEIGHT }}>
                      {geom ? (
                        <div
                          className="absolute rounded-md shadow-xs transition-opacity"
                          style={{
                            left: geom.x,
                            width: geom.width,
                            top: 5,
                            height: ROW_HEIGHT - 10,
                            backgroundColor: color,
                            opacity: row.draggable ? 1 : 0.65,
                            cursor: row.draggable ? "grab" : "default",
                          }}
                          onMouseDown={(e) => startDrag(e, row, "move")}
                          title={`${row.badge ? `${row.badge.label} · ` : ""}${row.key}: ${new Date(row.startDate!).toLocaleDateString()} → ${new Date(row.dueDate!).toLocaleDateString()}${row.draggable ? "" : " (rolled up from children)"}`}
                        >
                          {row.draggable && (
                            <>
                              <div
                                className="absolute left-0 top-0 h-full w-2 cursor-ew-resize hover:bg-black/20 rounded-l"
                                onMouseDown={(e) => {
                                  e.stopPropagation();
                                  startDrag(e, row, "resize-start");
                                }}
                              />
                              <div
                                className="absolute right-0 top-0 h-full w-2 cursor-ew-resize hover:bg-black/20 rounded-r"
                                onMouseDown={(e) => {
                                  e.stopPropagation();
                                  startDrag(e, row, "resize-end");
                                }}
                              />
                            </>
                          )}
                        </div>
                      ) : (
                        <span className="absolute left-2 top-2 text-[10px] italic text-muted-foreground/60 select-none">
                          No dates set
                        </span>
                      )}
                    </div>
                  );
                  y += ROW_HEIGHT;
                }
              }
              return elements;
            })()}

            {/* Dependency arrows */}
            <svg className="pointer-events-none absolute inset-0" width={chartWidth} height={chartHeight}>
              <defs>
                <marker id="timeline-arrow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
                  <path d="M0,0 L6,3 L0,6 Z" fill="#94a3b8" />
                </marker>
              </defs>
              {visibleDependencies.map((dep, i) => {
                const blocker = rowById.get(dep.blockerId);
                const blocked = rowById.get(dep.blockedId);
                if (!blocker || !blocked) return null;
                const fromGeom = barGeometry(blocker);
                const toGeom = barGeometry(blocked);
                if (!fromGeom || !toGeom) return null;
                const fromY = (yByRowId.get(dep.blockerId) ?? 0) + ROW_HEIGHT / 2;
                const toY = (yByRowId.get(dep.blockedId) ?? 0) + ROW_HEIGHT / 2;
                const fromX = fromGeom.x + fromGeom.width;
                const toX = toGeom.x;
                const midX = fromX + Math.max(12, (toX - fromX) / 2);
                const path =
                  toX >= fromX
                    ? `M ${fromX} ${fromY} L ${midX} ${fromY} L ${midX} ${toY} L ${toX - 4} ${toY}`
                    : `M ${fromX} ${fromY} L ${fromX + 12} ${fromY} L ${fromX + 12} ${(fromY + toY) / 2} L ${toX - 12} ${(fromY + toY) / 2} L ${toX - 12} ${toY} L ${toX - 4} ${toY}`;
                return <path key={i} d={path} fill="none" stroke="#94a3b8" strokeWidth={1.5} markerEnd="url(#timeline-arrow)" />;
              })}
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
}
